'use client'

import { useState } from 'react'
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react'

/**
 * SEC-2FA-3 (v153): the login-time second-factor prompt, shared by the
 * password path (login page) and the Google OAuth callback. Both flows show
 * this after the backend returns {status:"2fa_required"}; the caller owns the
 * re-submit (password re-POSTs the credentials + code, Google re-POSTs the
 * totp_handshake_token + code).
 *
 * Accepts a 6-digit authenticator code OR an 8-char one-time recovery code
 * (the backend verifier accepts either). Purely presentational + local input
 * state; no API calls here.
 */
export default function TotpPrompt({
  onSubmit,
  submitting,
  error,
  onCancel,
}: {
  onSubmit: (code: string) => void | Promise<void>
  submitting: boolean
  error?: string | null
  onCancel?: () => void
}) {
  const [code, setCode] = useState('')
  const [useRecovery, setUseRecovery] = useState(false)

  const trimmed = code.trim()
  // 6 digits for TOTP, up to 10 chars for a recovery code (backend bound).
  const valid = useRecovery ? trimmed.length >= 6 : /^\d{6}$/.test(trimmed)

  const handle = (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid || submitting) return
    void onSubmit(trimmed)
  }

  return (
    <form onSubmit={handle} className="space-y-5" noValidate>
      <div className="flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-terra-500 shrink-0 mt-0.5" />
        <div>
          <h2 className="text-ink-900 font-semibold text-lg">Two-factor authentication</h2>
          <p className="text-ink-700 text-sm mt-1">
            {useRecovery
              ? 'Enter one of your one-time recovery codes.'
              : 'Enter the 6-digit code from your authenticator app to continue.'}
          </p>
        </div>
      </div>

      {error ? (
        <div className="flex items-start gap-2 bg-terra-500/10 border border-terra-500/30 text-ink-900 rounded-lg p-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-terra-500" />
          <span>{error}</span>
        </div>
      ) : null}

      <div>
        <label htmlFor="totp-code" className="text-eyebrow uppercase tracking-widest text-ink-600 font-semibold block mb-2">
          {useRecovery ? 'Recovery code' : 'Authenticator code'}
        </label>
        <input
          id="totp-code"
          name="totp-code"
          type="text"
          inputMode={useRecovery ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder={useRecovery ? 'XXXXXXXX' : '123456'}
          maxLength={useRecovery ? 10 : 6}
          autoFocus
          aria-label={useRecovery ? 'Recovery code' : 'Authenticator code'}
          className="cream-input tracking-[0.3em] text-center text-lg"
        />
      </div>

      <button
        type="submit"
        disabled={!valid || submitting}
        className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Verifying...
          </>
        ) : (
          'Verify and continue'
        )}
      </button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => {
            setUseRecovery((v) => !v)
            setCode('')
          }}
          className="text-ink-600 hover:text-terra-600 transition-colors"
        >
          {useRecovery ? 'Use authenticator code' : 'Use a recovery code'}
        </button>
        {onCancel ? (
          <button type="button" onClick={onCancel} className="text-ink-600 hover:text-terra-600 transition-colors">
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  )
}
